import React from 'react';
import { T, NUMERIC, money } from '../customer/customers.theme';
import { EMPHASIS, grouped } from './dashboard.theme';
import { avatarTint } from '../../SignupAndLogin/login.theme';
import { initialsOf } from '../admin/adminApi';
import { orderMoment } from './dashboard.selectors';
import { Panel } from './DashboardUI';

/**
 * The lists: what sold, what did not, who rang it in, and what just happened.
 */

/* ── selling ────────────────────────────────────────────────────────────── */

export function SellingPanel({ rows, currency, basis, onBasis, subtitle }) {
  const top = rows.reduce((max, row) => Math.max(max, row[basis]), 0);

  return (
    <Panel
      title="Selling"
      subtitle={subtitle}
      action={
        <div style={{ display: 'flex', gap: 3, padding: 3, background: T.shade, borderRadius: T.radiusPill }}>
          {[
            { id: 'quantity', label: 'Plates' },
            { id: 'revenue', label: 'Revenue' },
          ].map((option) => {
            const active = basis === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => onBasis(option.id)}
                style={{
                  padding: '5px 11px',
                  background: active ? T.surface : 'transparent',
                  border: 'none',
                  borderRadius: T.radiusPill,
                  font: 'inherit',
                  fontSize: 11,
                  fontWeight: active ? 700 : 600,
                  color: active ? T.ink : T.inkFaint,
                  cursor: 'pointer',
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      }
    >
      {rows.length ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {rows.map((row, index) => (
            <div
              key={row.id}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                padding: '9px 0',
                borderBottom: index === rows.length - 1 ? 'none' : `1px solid ${T.hairline}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
                <span
                  title={row.name}
                  style={{
                    minWidth: 0,
                    fontSize: 13,
                    color: T.ink,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {row.name}
                </span>
                <span style={{ flexShrink: 0, fontSize: 12.5, fontWeight: 700, color: T.ink, ...NUMERIC }}>
                  {grouped(row.quantity)} · {money(row.revenue, currency)}
                </span>
              </div>
              <div style={{ height: 6, background: EMPHASIS.track, borderRadius: T.radiusPill }}>
                <div
                  style={{
                    width: `${top > 0 ? Math.max(2, (row[basis] / top) * 100) : 0}%`,
                    height: 6,
                    background: EMPHASIS.accent,
                    borderRadius: T.radiusPill,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 12.5, color: T.inkFaint }}>Nothing sold yet.</span>
      )}
    </Panel>
  );
}

/* ── not moving ─────────────────────────────────────────────────────────── */

/**
 * The quiet half of the menu.
 *
 * An item added yesterday looks as idle here as one nobody has ordered in a
 * year, so the subtitle names the period rather than implying a verdict.
 */
export function NotMovingPanel({ data, periodLabel }) {
  return (
    <Panel title="Not moving" subtitle={`Sold least ${periodLabel}`}>
      {data.rows.length ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {data.rows.map((row, index) => (
            <div
              key={row._id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 0',
                borderBottom: index === data.rows.length - 1 ? 'none' : `1px solid ${T.hairline}`,
              }}
            >
              <span
                title={row.name}
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13,
                  color: T.ink,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row.name}
              </span>
              <span
                style={{
                  flexShrink: 0,
                  fontSize: 12,
                  fontWeight: 700,
                  color: row.quantity ? T.inkFaint : T.danger,
                  ...NUMERIC,
                }}
              >
                {row.quantity ? `${grouped(row.quantity)} sold` : 'none'}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 12.5, color: T.inkFaint }}>Nothing on the menu yet.</span>
      )}

      {data.total ? (
        <div style={{ paddingTop: 12, fontSize: 11.5, color: T.inkFaint, lineHeight: 1.5 }}>
          {data.total - data.soldCount} of {data.total} items sold nothing.
        </div>
      ) : null}
    </Panel>
  );
}

/* ── who is serving ─────────────────────────────────────────────────────── */

/**
 * Ranked by the money each person rang in.
 *
 * The bars encode revenue and the label says so — this is the till's record of
 * who entered the order, not who carried the plate, and it is worth not
 * pretending otherwise on a screen a manager may read as a performance sheet.
 */
export function StaffPanel({ rows, currency }) {
  const top = rows.reduce((max, row) => Math.max(max, row.revenue), 0);

  return (
    <Panel title="Who is serving" subtitle="Revenue rung in, per person">
      {rows.length ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {rows.map((row, index) => {
            const tint = avatarTint(row.username || row.name || row._id);
            return (
              <div
                key={row._id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: index === rows.length - 1 ? 'none' : `1px solid ${T.hairline}`,
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    background: tint.bg,
                    borderRadius: T.radiusPill,
                    fontSize: 12,
                    fontWeight: 700,
                    color: tint.ink,
                    flexShrink: 0,
                  }}
                >
                  {initialsOf(row.name)}
                </span>

                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span
                    title={row.name}
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: T.ink,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {row.name}
                  </span>
                  <div style={{ height: 5, background: EMPHASIS.track, borderRadius: T.radiusPill }}>
                    <div
                      style={{
                        width: `${top > 0 ? Math.max(2, (row.revenue / top) * 100) : 0}%`,
                        height: 5,
                        background: EMPHASIS.accent,
                        borderRadius: T.radiusPill,
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, ...NUMERIC }}>
                    {money(row.revenue, currency)}
                  </span>
                  <span style={{ fontSize: 11, color: T.inkFaint, ...NUMERIC }}>
                    {row.orders} {row.orders === 1 ? 'order' : 'orders'} · {money(row.average, currency)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <span style={{ fontSize: 12.5, color: T.inkFaint }}>Nobody has rung anything in yet.</span>
      )}
    </Panel>
  );
}

/* ── latest orders ──────────────────────────────────────────────────────── */

const COLUMNS = '62px 62px minmax(0, 1fr) 46px 78px 76px';

const STATUS_STYLE = {
  paid: { bg: T.paidTint, border: '#BCE5CD', ink: T.paid, label: 'Paid' },
  completed: { bg: T.paidTint, border: '#BCE5CD', ink: T.paid, label: 'Paid' },
  invoiced: { bg: T.selectedBg, border: T.selectedBorder, ink: T.brandInk, label: 'Invoice' },
  pending: { bg: T.pendingTint, border: T.pendingBorder, ink: T.pendingInk, label: 'Pending' },
  preparing: { bg: T.pendingTint, border: T.pendingBorder, ink: T.pendingInk, label: 'Preparing' },
  ready: { bg: T.pendingTint, border: T.pendingBorder, ink: T.pendingInk, label: 'Ready' },
  served: { bg: T.pendingTint, border: T.pendingBorder, ink: T.pendingInk, label: 'Served' },
};

/**
 * The last few tickets.
 *
 * Canceled orders can never appear here: the orders endpoint filters them out
 * before they reach the till. Times come from `created_at` — the server's own
 * stamp — because the order's `date` is written by whichever client placed it
 * and is currently three hours out from the till and time-less from the phone.
 */
export function LatestOrders({ orders, currency, onOpenOrders }) {
  return (
    <Panel
      title="Latest orders"
      subtitle="Newest first"
      action={
        onOpenOrders ? (
          <button
            type="button"
            onClick={onOpenOrders}
            style={{
              padding: 0,
              background: 'transparent',
              border: 'none',
              font: 'inherit',
              fontSize: 11.5,
              fontWeight: 700,
              color: T.brandInk,
              cursor: 'pointer',
            }}
          >
            Open Orders →
          </button>
        ) : null
      }
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: COLUMNS,
          gap: 10,
          padding: '9px 0',
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, color: T.inkSoft }}>Order</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.inkSoft }}>Time</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.inkSoft }}>Waiter</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.inkSoft, textAlign: 'right' }}>Items</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.inkSoft, textAlign: 'right' }}>Total</span>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.inkSoft, textAlign: 'right' }}>Status</span>
      </div>

      {orders.length ? (
        orders.map((order, index) => {
          const status = STATUS_STYLE[String(order.orderStatus || '').toLowerCase()] || {
            bg: T.shade,
            border: T.border,
            ink: T.inkSoft,
            label: order.orderStatus || '—',
          };

          return (
            <div
              key={order._id || order.orderNumber}
              style={{
                display: 'grid',
                gridTemplateColumns: COLUMNS,
                gap: 10,
                alignItems: 'center',
                padding: '10px 0',
                borderBottom: index === orders.length - 1 ? 'none' : `1px solid ${T.hairline}`,
              }}
            >
              <span style={{ fontSize: 12.5, fontWeight: 700, color: T.ink, ...NUMERIC }}>
                #{order.orderNumber}
              </span>
              <span style={{ fontSize: 12.5, color: T.inkSoft, ...NUMERIC }}>
                {orderMoment(order).format('h:mm A')}
              </span>
              <span
                style={{
                  minWidth: 0,
                  fontSize: 12.5,
                  color: T.ink,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {(order.user && order.user.name) || '—'}
              </span>
              <span style={{ fontSize: 12.5, color: T.inkSoft, textAlign: 'right', ...NUMERIC }}>
                {(order.items || []).length}
              </span>
              <span style={{ fontSize: 12.5, fontWeight: 700, textAlign: 'right', color: T.ink, ...NUMERIC }}>
                {money(order.total, currency)}
              </span>
              <span
                style={{
                  justifySelf: 'end',
                  padding: '3px 10px',
                  background: status.bg,
                  border: `1px solid ${status.border}`,
                  borderRadius: T.radiusPill,
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: status.ink,
                  whiteSpace: 'nowrap',
                }}
              >
                {status.label}
              </span>
            </div>
          );
        })
      ) : (
        <div style={{ padding: '26px 0', textAlign: 'center' }}>
          <span style={{ fontSize: 12.5, color: T.inkFaint }}>No orders in this period.</span>
        </div>
      )}
    </Panel>
  );
}
